import { observer } from 'mobx-react-lite';
import * as React from 'react';
import ScriptManagementStore from '../stores/ScriptManagementStore';
import { Button, Layout, Popconfirm, Space, Table, Tooltip, Tag } from 'antd';
import LayoutHeader from '../../../components/LayoutHeader';
import { Content } from 'antd/lib/layout/layout';
import { Utils } from '../../common/services/Utils';
import { Script } from '../types';

type Props = {
    store: ScriptManagementStore;
};

const ScriptUsageCell: React.FC<{ store: ScriptManagementStore; scriptId: string }> = observer(
    ({ store, scriptId }) => {
        const usages = store.getScriptUsages(scriptId);

        if (store.isLoadingUsagesInProjects) {
            return <span style={{ color: '#999' }}>Loading...</span>;
        }

        if (usages.length === 0) {
            return <span style={{ color: '#999' }}>Not used</span>;
        }

        return (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {usages.map(usage => (
                    <Tag key={usage.projectId} color="blue" style={{ marginRight: 0 }}>
                        {usage.projectName}
                    </Tag>
                ))}
            </Space>
        );
    }
);

const ScriptManagementList: React.FC<Props> = ({ store }) => {
    React.useEffect(() => {
        if (store.scripts.length === 0) {
            store.loadScripts();
        }
    }, [store]);

    React.useEffect(() => {
        if (store.scripts.length > 0) {
            store.loadScriptUsagesInProjects();
        }
    }, [store, store.scripts]);

    const columns = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <span style={{ color: '#999', fontSize: '0.8rem' }}>{id}</span>
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: Script, b: Script) => a.name.localeCompare(b.name)
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'Created At',
            dataIndex: 'createDate',
            key: 'createDate',
            render: (date: string) => <span style={{ color: '#999' }}>{Utils.formatDateStringShort(date)}</span>,
            sorter: (a: Script, b: Script) => Utils.safeDateStringCompare(a.createDate, b.createDate)
        },
        {
            title: 'Updated At',
            dataIndex: 'updateDate',
            key: 'updateDate',
            render: (date: string | undefined) => (
                <span style={{ color: '#999' }}>{date ? Utils.formatDateStringShort(date) : '-'}</span>
            ),
            sorter: (a: Script, b: Script) => Utils.safeDateStringCompare(a.updateDate, b.updateDate)
        },
        {
            title: 'Changed By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (_: unknown, record: Script) => {
                const userId = record.updatedBy || record.createdBy;
                return store.getUserNameById(userId);
            },
            sorter: (a: Script, b: Script) => {
                const aUserId = a.updatedBy || a.createdBy || '';
                const bUserId = b.updatedBy || b.createdBy || '';
                return aUserId.localeCompare(bUserId);
            }
        },
        {
            title: 'Script Usage',
            key: 'usage',
            width: 200,
            render: (_: unknown, record: Script) => <ScriptUsageCell store={store} scriptId={record.id} />
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_: unknown, record: Script) => (
                <div className="row-actions-wrapper">
                    <Space size="middle">
                        <Tooltip title="Edit script">
                            <Button
                                type="link"
                                size="small"
                                data-id="button-edit-script"
                                icon={<i className="alpha-icon lg edit-icon" />}
                                onClick={() => store.navigateToEditScript(record.id)}
                            />
                        </Tooltip>
                        <Popconfirm
                            title="Are you sure you want to delete this script?"
                            onConfirm={() => store.deleteScript(record.id)}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Tooltip title="Delete script">
                                <Button
                                    type="link"
                                    size="small"
                                    data-id="button-delete-script"
                                    icon={<i className="alpha-icon lg delete-icon" />}
                                    danger
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                </div>
            )
        }
    ];

    return (
        <Layout style={{ height: '100%', background: 'white' }}>
            <LayoutHeader
                title="Script Management"
                buttons={[
                    <Button
                        key="create-script"
                        data-id="button-create-script"
                        type="primary"
                        size="large"
                        onClick={() => store.navigateToCreateScript()}
                    >
                        Create
                    </Button>
                ]}
            />
            <Layout className="script-management-list">
                <Content style={{ maxHeight: 'calc(100vh - 70px)', overflowY: 'auto' }}>
                    <Table
                        data-id="scripts-table"
                        className="alpha-portal-table"
                        dataSource={store.sortedScripts}
                        columns={columns}
                        loading={store.isLoadingScripts || store.isLoadingUsers}
                        rowKey="id"
                        pagination={{
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} scripts`
                        }}
                        sticky
                    />
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(ScriptManagementList);
